import { Button } from '@/components/ui/button';
import { Download, Loader2 } from 'lucide-react';
import { useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { format } from 'date-fns';

interface ChartMetadata {
  title: string;
  description?: string;
  dateRange?: string;
  additionalInfo?: Record<string, string>;
}

interface ExportAllChartsButtonProps {
  containerId: string;
  filename: string;
  metadata: ChartMetadata;
  label?: string;
  size?: 'default' | 'sm' | 'lg';
  variant?: 'default' | 'outline' | 'ghost';
}

export function ExportAllChartsButton({
  containerId,
  filename,
  metadata,
  label = 'Export All',
  size = 'default',
  variant = 'outline',
}: ExportAllChartsButtonProps) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const container = document.getElementById(containerId);

      if (!container) {
        console.error(`Container with ID ${containerId} not found`);
        return;
      }

      // Get all charts in the container
      const charts = container.querySelectorAll("[id^='chart-']");
      if (charts.length === 0) {
        console.error('No charts found in container');
        return;
      }

      // Prepare sanitized, formatted filename
      const timestamp = format(new Date(), 'yyyyMMdd-HHmmss');
      const sanitizedFilename = filename.replace(/[^a-z0-9-_]/gi, '-').toLowerCase();
      const pdfFilename = `${sanitizedFilename}_${timestamp}.pdf`;
      const pngFilename = `${sanitizedFilename}_${timestamp}.png`;

      // Create PDF with title page and chart pages
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
      });

      // Add title page
      pdf.setFontSize(22);
      pdf.text(metadata.title, 20, 30);

      // Add description
      if (metadata.description) {
        pdf.setFontSize(14);
        pdf.text(metadata.description, 20, 45);
      }

      // Add date range
      if (metadata.dateRange) {
        pdf.setFontSize(12);
        pdf.text(`Date Range: ${metadata.dateRange}`, 20, 55);
      }

      // Add additional metadata on title page
      if (metadata.additionalInfo) {
        let yPosition = 65;
        pdf.setFontSize(12);
        pdf.text('Report Information:', 20, yPosition);
        yPosition += 8;

        Object.entries(metadata.additionalInfo).forEach(([key, value]) => {
          // Check if we need to move to a new page
          if (yPosition > 270) {
            pdf.addPage();
            yPosition = 20;
          }

          // Handle long values by breaking them into multiple lines if needed
          if (value && value.length > 60) {
            const lines = [];
            let currentLine = '';
            const words = value.split(' ');
            for (const word of words) {
              if ((currentLine + ' ' + word).length <= 60) {
                currentLine = currentLine ? currentLine + ' ' + word : word;
              } else {
                lines.push(currentLine);
                currentLine = word;
              }
            }
            if (currentLine) lines.push(currentLine);

            pdf.text(`${key}:`, 20, yPosition);
            yPosition += 6;

            lines.forEach((line) => {
              pdf.text(`  ${line}`, 20, yPosition);
              yPosition += 6;
            });
          } else {
            pdf.text(`${key}: ${value || 'N/A'}`, 20, yPosition);
            yPosition += 6;
          }
        });
      }

      // Add generation timestamp
      const pageHeight = pdf.internal.pageSize.getHeight();
      pdf.setFontSize(8);
      pdf.text(`Report generated: ${format(new Date(), 'PPP pp')}`, 20, pageHeight - 10);

      // Process each chart
      for (const chart of Array.from(charts)) {
        // Add a new page for each chart
        pdf.addPage();

        // Render chart to canvas
        const canvas = await html2canvas(chart as HTMLElement, {
          backgroundColor: null,
          scale: 2,
        });

        // Calculate dimensions
        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;
        const pageWidth = pdf.internal.pageSize.getWidth();

        // Calculate image dimensions to fit on page
        const imageWidth = pageWidth - 20;
        const imageHeight = (canvasHeight * imageWidth) / canvasWidth;

        // Add chart title if available
        const chartTitle =
          chart.getAttribute('data-chart-title') ||
          chart.querySelector('h3')?.textContent ||
          chart.id.replace('chart-', '');

        pdf.setFontSize(14);
        pdf.text(chartTitle, 10, 15);

        // Add image of chart
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 20, imageWidth, imageHeight);

        // Try to extract and add chart-specific metadata
        const metadataElement = chart.querySelector('[data-date-range]');
        if (metadataElement) {
          const dateRange = metadataElement.getAttribute('data-date-range');
          if (dateRange) {
            pdf.setFontSize(10);
            pdf.text(`Date Range: ${dateRange}`, 10, 25 + imageHeight);
          }
        }
      }

      // Save the PDF
      pdf.save(pdfFilename);

      // Also create a PNG of the whole container
      const containerCanvas = await html2canvas(container, {
        backgroundColor: null,
        scale: 1.5,
      });

      const link = document.createElement('a');
      link.download = pngFilename;
      link.href = containerCanvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error exporting charts:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button variant={variant} size={size} onClick={handleExport} disabled={isExporting} className="min-w-[120px]">
      {isExporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
      {isExporting ? 'Exporting...' : label}
    </Button>
  );
}
